import { uploadImage } from 'lib/api';
import { wrapWithHttp } from 'lib/utils/functions';
import { MAX_IMAGE_HEIGHT, MAX_IMAGE_WIDTH } from '../constants/style';
import { annotationSteps, getInitialTemplate } from '../constants/templates';
import { getTypeValues } from '../constants/types';
import { TFunction } from 'i18next';

type PrepareAnnotationProps = {
  data: any;
  time: ReadonlyArray<number>;
  selectedImage?: File;
  userData: any;
};

export const hasAnnotationCRUDPrivileges = (
  user: any,
  video: any,
  annotation?: any
) => {
  const isCompanyAnnotation = annotation?.isCompanyAnnotation;
  const isAdmin = user.access >= 3;
  const hasAutomotiveEditPrivilege = user.automotiveRole > 1;
  const isOwner = Number(video.userId) === Number(user.id);
  const hasAccessToSharedFolder =
    !!video.folderAccess && video.folderAccess === 'edit';

  return isCompanyAnnotation
    ? isAdmin
    : isAdmin ||
        isOwner ||
        hasAutomotiveEditPrivilege ||
        hasAccessToSharedFolder;
};

export const capitalize = (value: string | undefined) => {
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1);
};

export const calculateImageDimensions = (
  width: number,
  height: number,
  ratio = 0
) => {
  if (!ratio) {
    ratio = width / height;
  }

  if (width > height) {
    width = width > MAX_IMAGE_WIDTH ? MAX_IMAGE_WIDTH : width;
    height =
      Math.floor(width / ratio) > MAX_IMAGE_HEIGHT
        ? MAX_IMAGE_HEIGHT
        : Math.floor(width / ratio);
  } else {
    height = height > MAX_IMAGE_HEIGHT ? MAX_IMAGE_HEIGHT : height;
    width =
      Math.floor(height * ratio) > MAX_IMAGE_WIDTH
        ? MAX_IMAGE_WIDTH
        : Math.floor(height * ratio);
  }

  return {
    width,
    height,
    ratio,
  };
};

export const getImageRatio = (annotation: any) => {
  let imageRatio = 0;
  if (annotation.type === 'image') {
    imageRatio = annotation.imageWidthPercentage
      ? parseInt(annotation.imageWidthPercentage, 10) /
        parseInt(annotation.imageHeightPercentage, 10)
      : annotation.imageWidth / annotation.imageHeight;
  }

  return imageRatio;
};

export const determineAnnotationValue = (keyword: string, t: TFunction) => {
  const typeValues = getTypeValues(t);
  const annotationValue = typeValues.filter(
    typeValue => typeValue.text === keyword
  );
  if (!annotationValue.length) return keyword;
  return annotationValue[0].value;
};

export const determineAnnotationText = (keyword: string, t: TFunction) => {
  const typeValues = getTypeValues(t);
  const annotationValue = typeValues.filter(
    typeValue => typeValue.value === keyword
  );
  if (!annotationValue.length) return keyword;
  return annotationValue[0].text;
};

export const prepareAnnotation = async ({
  data,
  time,
  selectedImage,
  userData,
}: PrepareAnnotationProps) => {
  const annotationData = {
    ...data,
  };
  if (annotationData.url) {
    annotationData.url = wrapWithHttp(annotationData.url);
  }
  annotationData.startTime = time[0];
  annotationData.endTime = time[1];

  const isImage = annotationData.type === 'image';
  if (isImage) {
    if (annotationData.useImagePercentage) {
      delete annotationData.imageWidth;
      delete annotationData.imageHeight;
    } else {
      delete annotationData.imageWidthPercentage;
      delete annotationData.imageHeightPercentage;
    }
    if (selectedImage) {
      annotationData.imageUrl = await uploadImage(
        selectedImage,
        userData.userId
      );
    }
  }

  if (annotationData.templateId) delete annotationData.templateId;

  return annotationData;
};

type SelectedTemplateProps = {
  template: any;
  setStep: (arg: string) => void;
  setSelectedTemplate: (arg: object) => void;
  t: TFunction;
};

export const selectTemplate = ({
  template,
  setStep,
  setSelectedTemplate,
  t,
}: SelectedTemplateProps) => {
  const temp = { ...template };
  const initialTemplate = getInitialTemplate(t);
  temp.imageRatio = getImageRatio(temp);
  temp.useImagePercentage = !!temp.imageWidthPercentage;
  delete temp.customerId;
  setStep(annotationSteps.STYLE);
  setSelectedTemplate({
    ...initialTemplate,
    ...temp,
    url: temp.url || '',
    email: temp.email || '',
    phone: temp.phone || '',
  });
};

export const calculatePercentageOnChange = (
  widthPercentage: string,
  imageRatio: number,
  setFieldValue: Function
) => {
  const { width, height } = calculateImageDimensions(
    parseInt(widthPercentage, 10),
    0,
    imageRatio
  );
  setFieldValue('imageWidthPercentage', width + '%');
  setFieldValue('imageHeightPercentage', height + '%');
};

export const calculateDimensionsOnChange = (
  currentWidth: number,
  currentHeight: number,
  imageRatio: number,
  setFieldValue: Function
) => {
  const { width, height } = calculateImageDimensions(
    currentWidth,
    currentHeight,
    imageRatio
  );
  setFieldValue('imageWidth', width);
  setFieldValue('imageHeight', height);
};
